import { Controller } from "@hotwired/stimulus"

const COLORS = ["#1d5dea", "#443ae3", "#b81b58", "#11a046", "#d4af37", "#ff6f1b", "#0791b2"]
const DURATION = 8000
const FADE_START = 7000
const COUNT = 150

const STORAGE_KEY = "vasDashboardParty"
const WINDOW_MS = 60 * 60 * 1000
const BURST_INTERVAL_MS = 2 * 60 * 1000

export default class extends Controller {
  static values = { auto: Boolean, period: String }

  connect() {
    if (!this.autoValue) return

    this.evaluate()

    const targets = document.getElementById("dashboard-profit-targets")
    if (targets) {
      this.observer = new MutationObserver(() => this.evaluate())
      this.observer.observe(targets, { childList: true, subtree: true })
    }
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.burstTimer) clearInterval(this.burstTimer)
  }

  evaluate() {
    if (this.celebrating || !this.targetMet() || this.alreadyCelebrated()) return

    window.localStorage.setItem(STORAGE_KEY, this.periodValue)
    this.startCelebration()
  }

  targetMet() {
    const canvas = document.querySelector(
      "#dashboard-profit-targets canvas[data-dashboard-ring-chart-monthly-target-met-value]"
    )
    return canvas?.dataset.dashboardRingChartMonthlyTargetMetValue === "true"
  }

  alreadyCelebrated() {
    return window.localStorage.getItem(STORAGE_KEY) === this.periodValue
  }

  startCelebration() {
    this.celebrating = true
    this.windowEnd = performance.now() + WINDOW_MS
    this.launch()

    this.burstTimer = setInterval(() => {
      if (performance.now() >= this.windowEnd) {
        clearInterval(this.burstTimer)
        return
      }
      this.launch()
    }, BURST_INTERVAL_MS)
  }

  launch() {
    if (document.getElementById("dashboard-confetti-canvas")) return

    const canvas = document.createElement("canvas")
    canvas.id = "dashboard-confetti-canvas"
    canvas.style.position = "fixed"
    canvas.style.top = "0"
    canvas.style.left = "0"
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    canvas.style.pointerEvents = "none"
    canvas.style.zIndex = "9999"
    document.body.appendChild(canvas)

    const ctx = canvas.getContext("2d")
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize)

    const particles = Array.from({ length: COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * -canvas.height * 0.5,
      vx: (Math.random() - 0.5) * 2.5,
      vy: Math.random() * 2 + 1,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.2,
      size: Math.random() * 5 + 4,
      shape: Math.random() > 0.4 ? "rect" : "circle",
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      opacity: 1
    }))

    const startTime = performance.now()
    let rafId

    const tick = (now) => {
      const elapsed = now - startTime
      if (elapsed >= DURATION) {
        window.removeEventListener("resize", resize)
        canvas.remove()
        return
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const fade = elapsed > FADE_START
        ? 1 - (elapsed - FADE_START) / (DURATION - FADE_START)
        : 1

      for (const p of particles) {
        p.vy += 0.18
        p.x += p.vx
        p.y += p.vy
        p.rotation += p.rotationSpeed

        if (p.y > canvas.height) {
          p.y = -p.size * 2
          p.x = Math.random() * canvas.width
          p.vy = Math.random() * 2 + 1
        }

        ctx.save()
        ctx.globalAlpha = fade
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.fillStyle = p.color

        if (p.shape === "circle") {
          ctx.beginPath()
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
          ctx.fill()
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        }

        ctx.restore()
      }

      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)

    setTimeout(() => {
      cancelAnimationFrame(rafId)
      window.removeEventListener("resize", resize)
      canvas.remove()
    }, DURATION + 100)
  }
}
